import createTheme from "@material-ui/core/styles/createTheme";
import createBreakpoints from "@material-ui/core/styles/createBreakpoints";

const breakpoints = createBreakpoints({});

/**
 * Brand tokens — the single source of truth for every color in the app.
 * Components must consume these through the theme (or this export for
 * gradients/one-offs the palette can't express) rather than hardcoding hexes.
 */
export const brand = {
  navy: "#1E4E77",
  navyDark: "#123B5D",
  navyLight: "#2682B9",
  green: "#008F6D",
  greenDark: "#00755A",
  greenTint: "#E6F4F0",
  orange: "#F37748",
  orangeTint: "#FFF2EB",
  plum: "#694D75",
  textHigh: "#2A2A2A",
  textMedium: "#585D5F",
  border: "#C7C7C7",
  divider: "#E6E6E6",
  background: "#F2F2F2",
  navyGradient: "linear-gradient(135deg, #123B5D, #1E4E77)",
  greenGradient: "linear-gradient(135deg, #00755A, #008F6D)",
  cardShadow: "0 2px 12px rgba(18, 59, 93, 0.08)",
  cardShadowHover: "0 6px 20px rgba(18, 59, 93, 0.16)",
};

const fontFamily = "'Open Sans', sans-serif";

const palette = {
  primary: {
    main: brand.navy,
    dark: brand.navyDark,
    light: brand.navyLight,
    contrastText: "#fff",
  },
  secondary: {
    main: brand.orange,
    contrastText: "#fff",
  },
  success: {
    main: brand.green,
    dark: brand.greenDark,
    contrastText: "#fff",
  },
  warning: {
    main: brand.plum,
  },
  text: {
    primary: brand.textHigh,
    secondary: brand.textMedium,
  },
  divider: brand.divider,
  background: {
    default: brand.background,
    paper: "#fff",
  },
};

const theme = createTheme({
  palette: palette,
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: fontFamily,
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      color: brand.textHigh,
      [breakpoints.down("sm")]: {
        fontSize: "2rem",
      },
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 600,
      color: brand.textHigh,
      [breakpoints.down("sm")]: {
        fontSize: "1.625rem",
      },
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 600,
      color: brand.textHigh,
      [breakpoints.down("sm")]: {
        fontSize: "1.5rem",
      },
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      color: brand.textHigh,
      [breakpoints.down("sm")]: {
        fontSize: "1.25rem",
      },
    },
    h5: {
      fontSize: "1.125rem",
      fontWeight: 600,
      color: brand.textHigh,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      color: brand.textHigh,
    },
    subtitle1: {
      textAlign: "center",
      fontSize: "22px",
      color: brand.textMedium,
      marginBottom: "48px",
      [breakpoints.down("sm")]: {
        fontSize: "18px",
      },
    },
    subtitle2: {
      fontStyle: "italic",
      fontSize: "14px",
      color: brand.textMedium,
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "14px",
      color: brand.textMedium,
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: "0.2px",
    },
  },
  overrides: {
    MuiLink: {
      root: {
        color: palette.secondary.main,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 8,
        padding: "8px 20px",
        transition: "all 200ms ease",
      },
      containedPrimary: {
        backgroundColor: brand.navy,
        boxShadow: "0 2px 8px rgba(18, 59, 93, 0.24)",
        "&:hover": {
          backgroundColor: brand.navyDark,
          boxShadow: "0 4px 12px rgba(18, 59, 93, 0.32)",
        },
      },
      outlinedPrimary: {
        borderColor: brand.navy,
        color: brand.navy,
        "&:hover": {
          border: `1px solid ${brand.navyDark}`,
          backgroundColor: "rgba(30, 78, 119, 0.04)",
        },
      },
      containedSecondary: {
        color: "#fff",
      },
    },
    MuiPaper: {
      elevation1: {
        boxShadow: brand.cardShadow,
      },
      elevation2: {
        boxShadow: brand.cardShadow,
      },
    },
    MuiCard: {
      root: {
        borderRadius: 12,
        boxShadow: brand.cardShadow,
      },
    },
    MuiCardContent: {
      root: {
        padding: "24px",
        "&:last-child": {
          paddingBottom: "24px",
        },
      },
    },
    MuiCardActions: {
      root: {
        padding: "16px 24px",
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 8,
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: brand.navyLight,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: brand.navy,
        },
      },
    },
    MuiInputLabel: {
      outlined: {
        "&.Mui-focused": {
          color: brand.navy,
        },
      },
    },
    MuiFormControl: {
      root: {
        marginBottom: "16px",
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 12,
        padding: "8px",
      },
    },
    MuiAccordion: {
      root: {
        borderRadius: 12,
        boxShadow: brand.cardShadow,
        "&:before": {
          display: "none",
        },
        "&.Mui-expanded": {
          margin: "12px 0",
        },
        "&:first-child": {
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        },
        "&:last-child": {
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
        },
      },
    },
    MuiTableCell: {
      head: {
        color: brand.navy,
        fontWeight: 600,
      },
    },
    MuiAppBar: {
      colorPrimary: {
        background: brand.navyGradient,
      },
    },
    MuiDrawer: {
      paper: {
        borderRight: "none",
        boxShadow: "2px 0 8px rgba(18, 59, 93, 0.06)",
      },
    },
    MuiListItem: {
      root: {
        "&.Mui-selected": {
          backgroundColor: "rgba(30, 78, 119, 0.08)",
          color: brand.navy,
          "& .MuiListItemIcon-root": {
            color: brand.navy,
          },
        },
      },
    },
    MuiTooltip: {
      tooltip: {
        backgroundColor: brand.navyDark,
        fontSize: "12px",
      },
    },
  },
});

export default theme;
